import {
  Power,
  RotateCw,
  SkipForward,
  SlidersHorizontal,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useState, type ReactNode } from "react";

import { Button, buttonVariants } from "../../design-system/button.js";
import { cn } from "../../design-system/cn.js";
import { TonePill } from "../../design-system/feed-state.js";
import { ConfirmStrip } from "../../design-system/inline-confirm.js";
import { useNow } from "../../lib/use-now.js";
import { LiveBars } from "../../player/artwork.js";
import { useFeedPlayback, usePlayer } from "../../player/player-provider.js";
import { AiredProgress, PlayButton } from "../../player/transport.js";
import { useFeedActions } from "./use-feed-actions.js";
import type { FeedConsoleRow } from "./console-model.js";

/**
 * One feed, as a row of the console.
 *
 * It answers, left to right, the three questions the operator has about every feed
 * he owns: what is it and is it well, what is on it and what is next, and what can I
 * do to it right now. Ten feeds are ten of these; one feed is one of these. Nothing
 * on this card knows which case it is in.
 *
 * The card is not a link — it contains six controls, and wrapping controls in a link
 * is a trap for a keyboard and a lie to a screen reader. The feed's name is the link.
 */
export function ConsoleCard({
  onChanged,
  row,
}: {
  onChanged: () => void;
  row: FeedConsoleRow;
}) {
  const { feed, live, next, status } = row;
  const { play, stop } = usePlayer();
  const { isCurrent, state: playback } = useFeedPlayback(feed.id);
  const { busy, dismiss, outcome, run } = useFeedActions(feed, onChanged);
  const [asking, setAsking] = useState(false);
  const airing = live?.nowPlaying ?? null;
  const now = useNow(1000, airing !== null);
  const streamUrl = live?.streamUrl ?? null;
  const listening = isCurrent && playback === "playing";
  // A feed that is switched off runs no engine, so there is nothing for a control
  // verb to reach. Saying so on the control is honester than a 503 after the press.
  const offReason = feed.enabled ? undefined : "This feed is switched off.";

  return (
    <article
      className={cn(
        "rounded-md border bg-surface transition-colors",
        row.attention
          ? "border-[color-mix(in_oklch,var(--warn)_30%,var(--border))]"
          : "border-border",
        !feed.enabled && "bg-bg-2",
      )}
    >
      <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,17rem)_minmax(0,1fr)_auto] lg:items-start lg:gap-6">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <Link
              className="truncate text-sm font-semibold text-ink underline-offset-4 hover:underline"
              to={`/feeds/${feed.id}`}
            >
              {feed.name}
            </Link>
            {listening ? <LiveBars className="h-2.5 text-accent-text" /> : null}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
            <TonePill
              label={status.label}
              pulse
              title={status.detail}
              tone={status.tone}
            />
            {row.attention ? (
              <span
                className={cn(
                  "min-w-0 truncate text-xs",
                  status.tone === "err" ? "text-err" : "text-warn",
                )}
                title={status.detail}
              >
                {status.detail}
              </span>
            ) : feed.description ? (
              <span className="min-w-0 truncate text-xs text-ink-3">
                {feed.description}
              </span>
            ) : null}
          </div>
          {row.deliveries.label ? (
            <p className="mt-2 font-mono text-[0.6875rem] text-ink-3">
              {row.deliveries.label}
            </p>
          ) : null}
        </div>

        <div className="grid min-w-0 gap-2">
          <TrackLine
            label="Now"
            subtitle={airing?.artist ?? ""}
            title={
              row.liveLoading
                ? "…"
                : (airing?.title ??
                  (row.liveError ? "Not reachable" : "Nothing airing"))
            }
            tone={airing ? "live" : "quiet"}
          >
            {airing ? (
              <AiredProgress
                className="mt-1.5 max-w-sm"
                now={now}
                snapshot={live}
              />
            ) : null}
          </TrackLine>
          <TrackLine
            label="Next"
            subtitle={next?.artist ?? ""}
            title={next?.title ?? "Nothing planned"}
            tone="quiet"
          />
        </div>

        <div className="flex flex-wrap items-center gap-1.5 lg:justify-end">
          <PlayButton
            className="mr-1"
            disabled={!streamUrl}
            feedName={feed.name}
            onActivate={() => {
              if (!streamUrl) return;
              if (isCurrent && playback !== "idle" && playback !== "error")
                stop();
              else play({ feedId: feed.id, feedName: feed.name, streamUrl });
            }}
            size="small"
            state={playback}
            tone="quiet"
          />
          <IconAction
            busy={busy === "skip"}
            disabled={!feed.enabled}
            icon={<SkipForward aria-hidden="true" />}
            label={`Skip ${feed.name}`}
            onClick={() => {
              run("skip");
            }}
            title={offReason}
          />
          <IconAction
            busy={busy === "restart"}
            disabled={!feed.enabled}
            icon={<RotateCw aria-hidden="true" />}
            label={`Restart ${feed.name}`}
            onClick={() => {
              run("restart");
            }}
            title={offReason}
          />
          <IconAction
            busy={busy === "enable" || busy === "disable"}
            className={feed.enabled ? undefined : "text-accent-text"}
            icon={<Power aria-hidden="true" />}
            label={
              feed.enabled ? `Disable ${feed.name}` : `Enable ${feed.name}`
            }
            onClick={() => {
              run(feed.enabled ? "disable" : "enable");
            }}
          />
          <Link
            aria-label={`Configure ${feed.name}`}
            className={buttonVariants({ size: "icon-small", variant: "ghost" })}
            title="Configure"
            to={`/feeds/${feed.id}/settings`}
          >
            <SlidersHorizontal aria-hidden="true" />
          </Link>
          <IconAction
            busy={busy === "delete"}
            className="text-ink-3 hover:text-err"
            icon={<Trash2 aria-hidden="true" />}
            label={`Delete ${feed.name}`}
            onClick={() => {
              setAsking(true);
            }}
          />
        </div>
      </div>

      {/* The card says nothing at its foot until there is something to say: a
          question about deleting it, or what the API answered to the last verb. */}
      {asking || outcome ? (
        <div className="border-t border-border px-4 py-2.5">
          {asking ? (
            <ConfirmStrip
              busy={busy === "delete"}
              confirmLabel="Delete feed"
              onCancel={() => {
                setAsking(false);
              }}
              onConfirm={() => {
                run("delete");
              }}
              question={`Delete ${feed.name}?`}
            >
              Deletes the feed, its deliveries and its play history. Your
              playlists and schedules aren't affected.
            </ConfirmStrip>
          ) : outcome ? (
            <button
              className={cn(
                "min-w-0 text-left text-xs",
                outcome.tone === "ok" && "text-ink-3",
                outcome.tone === "warn" && "text-warn",
                outcome.tone === "err" && "text-err",
              )}
              onClick={dismiss}
              title="Dismiss"
              type="button"
            >
              {outcome.text}
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function TrackLine({
  children,
  label,
  subtitle,
  title,
  tone,
}: {
  children?: ReactNode;
  label: string;
  subtitle: string;
  title: string;
  tone: "live" | "quiet";
}) {
  return (
    <div className="flex min-w-0 items-baseline gap-3">
      <span className="w-8 shrink-0 font-mono text-[0.5625rem] tracking-[0.14em] text-ink-3 uppercase">
        {label}
      </span>
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            "truncate text-sm",
            tone === "live" ? "text-ink" : "text-ink-2",
          )}
        >
          {title}
        </div>
        {subtitle ? (
          <div className="truncate text-xs text-ink-3">{subtitle}</div>
        ) : null}
        {children}
      </div>
    </div>
  );
}

function IconAction({
  busy,
  className,
  disabled = false,
  icon,
  label,
  onClick,
  title,
}: {
  busy: boolean;
  className?: string | undefined;
  disabled?: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
  title?: string | undefined;
}) {
  return (
    <Button
      aria-label={label}
      className={cn(
        busy && "animate-[radio-pulse_1.2s_ease-in-out_infinite]",
        className,
      )}
      disabled={disabled || busy}
      onClick={onClick}
      size="icon-small"
      title={title ?? label}
      variant="ghost"
    >
      {icon}
    </Button>
  );
}
